import {
  groupLayout,
  interClubMatchCount,
  partnerLayoutSpec,
  partnerMatchCount,
  roundRobinMatchCount,
  tierMatchCount,
  type GroupsConfig,
  type PhaseConfig,
  type SingleElimConfig,
} from '@airmail/engine';
import { t } from '@/i18n';
import { tm } from '@/i18n/engine';

/** Turniermodi im Kickertool-Muster (docs/11 §3) — für Assistent und Einstellungen */
export type ModeKey = 'groups_ko' | 'rr_ko' | 'roundrobin' | 'ko' | 'club_rr' | 'groups_ko_rounds' | 'partner_ko';

export interface ModeInfo {
  key: ModeKey;
  name: string;
  short: string;
  /** Ein Satz für die Kachel im Assistenten und unter der Auswahl in den Einstellungen */
  kurz: string;
  /** Die ganze Erklärung — im Assistenten einmal unter dem Raster für den gewählten Modus */
  description: string;
  /** Der Standard für die meisten Turniere — bekommt im Assistenten die Marke „Empfohlen" */
  empfohlen?: boolean;
}

export const MODES: ModeInfo[] = [
  {
    key: 'groups_ko',
    name: 'Gruppen → KO',
    short: 'Gruppen + KO',
    kurz: 'Gruppenphase jeder gegen jeden, dann KO mit den Besten. Der Standard für 8–64 Teams.',
    description: 'Gruppenphase jeder gegen jeden, danach KO-Runde mit den Besten je Gruppe. Der Standard für 8–64 Teams.',
    empfohlen: true,
  },
  {
    key: 'rr_ko',
    name: 'Jeder gegen Jeden → KO',
    short: 'RR + KO',
    kurz: 'Alle gegen alle in einer Gruppe, dann KO mit den besten 8 bis 20.',
    description: 'Eine Gruppe, alle spielen gegen alle — danach spielen die besten 8, 12, 16 oder 20 im KO-System weiter (Kickertool-Muster). Bei 12 oder 20 bekommen die Bestplatzierten Freilose.',
  },
  {
    key: 'groups_ko_rounds',
    name: 'Gruppen → KO Runde für Runde',
    short: 'Gruppen + KO-Runden',
    kurz: 'Gruppenphase, danach legt die Leitung vor jeder KO-Runde fest, wie viele Teams weiterkommen.',
    description:
      'Gruppenphase wie gewohnt. Danach entscheidet die Turnierleitung vor jeder Runde nach Uhrzeit und Lage, wie viele Teams weiterkommen — mal aufstocken, mal reduzieren. Gepaart wird cross (Bester gegen Schlechtesten); jede Runde lässt sich an einen Spielort binden.',
  },
  {
    key: 'roundrobin',
    name: 'Jeder gegen Jeden',
    short: 'Round Robin',
    kurz: 'Eine Gruppe, alle gegen alle. Für bis zu ~8 Teams oder Ligaabende.',
    description: 'Eine Gruppe, alle spielen gegen alle. Für bis zu ~8 Teams oder Ligaabende.',
  },
  {
    key: 'ko',
    name: 'KO-Runde',
    short: 'KO',
    kurz: 'Direkt ins Bracket, Verlierer scheiden aus — schnell, aber wenig Spiele je Team.',
    description: 'Direkt ins Bracket, Verlierer scheiden aus. Schnell, aber jedes Team hat mindestens nur ein Spiel.',
  },
  {
    key: 'partner_ko',
    name: 'Wechselnde Partner → KO',
    short: 'Partnerwechsel',
    kurz: 'Einzeln gemeldet: in der Gruppe spielt jeder mit jedem als Partner, Siege und Punkte zählen je Spieler. Die Besten gehen einzeln ins KO.',
    description:
      'Jeder meldet einzeln. In der Gruppe bilden je zwei Spieler für ein Spiel ein Doppel, Spiel für Spiel mit anderem Partner (im Padel „Americano“) — bei 5 Spielern z. B. C/D gegen E/B, D/E gegen A/C … Jeder Spieler bekommt Sieg und Punkte seines Doppels gutgeschrieben; gewertet wird erst nach Siegen, dann nach Punkten. Danach geht es wie gewohnt weiter: die Besten je Gruppe und auf Wunsch die besten Nächstplatzierten spielen einzeln im KO.',
  },
  {
    key: 'club_rr',
    name: 'Liga-Modus',
    short: 'Liga',
    kurz: 'Feste Paare je Mannschaft spielen gegen alle Paare der anderen Mannschaften, nie gegen die eigene.',
    description:
      'Mehrere Mannschaften mit je mehreren festen Paaren („Ceva 1", „Ceva 2" …). Jedes Paar spielt gegen alle Paare der anderen Mannschaften, nie gegen die eigene. Wertung je Paar, Mannschaft und Spieler; Spieltage lassen sich in eine Saison übernehmen.',
  },
];

/**
 * Anzeige-Reihenfolge nach Häufigkeit: das Übliche zuerst, das Besondere zum
 * Schluss. Nur die Darstellung — Schlüssel, Vorgaben (`groups_ko`) und die
 * Demo der Tour hängen nicht daran.
 */
export const MODE_ORDER: readonly ModeKey[] = ['groups_ko', 'rr_ko', 'ko', 'roundrobin', 'groups_ko_rounds', 'partner_ko', 'club_rr'];

/** `MODES` in Anzeige-Reihenfolge (Assistent, Einstellungen) */
export const MODES_GEORDNET: ModeInfo[] = MODE_ORDER.map((key) => MODES.find((m) => m.key === key) as ModeInfo);

export function modeInfo(key: ModeKey): ModeInfo {
  return MODES.find((m) => m.key === key) as ModeInfo;
}

export interface ModeOptions {
  groupSize: number;
  qualifiersPerGroup: number;
  /** Gruppen → KO: zusätzlich die besten Nächstplatzierten gruppenübergreifend (0 = keine) */
  bestRemaining: number;
  /** Jeder gegen Jeden → KO: so viele Teams spielen im KO weiter */
  koTeams: number;
  thirdPlaceMatch: boolean;
  avoidSameClub: boolean;
  /**
   * Liga-Modus: Leistungsgruppen nach der Nummer im Teamnamen (docs/13 §7,
   * Harald 09/2026) — „Ceva 1“, „Nucetto 1“ … in Gruppe 1. Standard aus: eine
   * Gruppe, jedes Paar gegen alle Paare der anderen Mannschaften.
   */
  leistungsgruppen: boolean;
  /**
   * Wechselnde Partner (Harald, 10/2026): `all` = jedes Paar gegen jedes andere
   * Paar („jeder mit jedem gegen jeden mit jedem“, 5 Spieler → 15 Spiele),
   * `once` = jedes Paar einmal zusammen (5 Spieler → 5 Spiele)
   */
  partners: 'once' | 'all';
}

export const DEFAULT_MODE_OPTIONS: ModeOptions = {
  groupSize: 4,
  qualifiersPerGroup: 2,
  bestRemaining: 0,
  koTeams: 8,
  thirdPlaceMatch: true,
  avoidSameClub: true,
  leistungsgruppen: false,
  // Standard „jedes Paar einmal“ (Daniel, 10/2026): „gegen jedes Paar“ hat bei 6 Spielern 45 Spiele
  partners: 'once',
};

/** Gruppengrößen zur Auswahl — mit wechselnden Partnern braucht es mindestens 4 */
export function groupSizeChoices(mode: ModeKey): number[] {
  return mode === 'partner_ko' ? [4, 5, 6, 7, 8, 9] : [3, 4, 5, 6];
}

/** Spiele je Spieler in einer Gruppe mit wechselnden Partnern */
export function partnerGamesPerPlayer(n: number, partners: 'once' | 'all'): number {
  if (n < 4) return 0;
  return partners === 'all' ? ((n - 1) * (n - 2) * (n - 3)) / 2 : n - 1;
}
export const KO_TEAM_CHOICES = [4, 8, 12, 16, 20, 24, 32];

export function phaseConfigsFor(mode: ModeKey, o: ModeOptions): PhaseConfig[] {
  const groups = (over: Partial<GroupsConfig>): GroupsConfig => ({
    type: 'groups',
    layout: { groupSize: o.groupSize },
    distribution: 'snake',
    seeding: 'teamSeed',
    doubleRound: false,
    qualifiersPerGroup: o.qualifiersPerGroup,
    bestRemaining: 0,
    stripLastWhenUneven: false,
    constraints: { avoidSameClub: o.avoidSameClub, keepApart: [] },
    ...over,
  });
  const ko = (over: Partial<SingleElimConfig>): SingleElimConfig => ({ type: 'single_elim', thirdPlaceMatch: o.thirdPlaceMatch, seedingSource: 'previousPhase', separateGroups: true, ...over });
  switch (mode) {
    case 'groups_ko':
      return [groups({ bestRemaining: Math.max(0, o.bestRemaining) }), ko({})];
    case 'rr_ko':
      return [groups({ layout: { groupCount: 1 }, qualifiersPerGroup: Math.max(2, o.koTeams) }), ko({ separateGroups: false })];
    case 'groups_ko_rounds':
      // Keine feste Qualifikantenzahl: die Leitung schneidet vor jeder Runde selbst ab (docs/notes/ko-runden.md)
      return [groups({ qualifiersPerGroup: 0 }), { type: 'ko_rounds', pairing: 'cross', thirdPlaceMatch: o.thirdPlaceMatch }];
    case 'roundrobin':
      return [groups({ layout: { groupCount: 1 }, qualifiersPerGroup: 0 })];
    case 'club_rr':
      // Nur eingecheckte Teams auslosen: der Kader der Saison ist komplett drin, gespielt wird mit denen, die da sind (docs/13 §4)
      return [
        groups({
          layout: { groupCount: 1 },
          qualifiersPerGroup: 0,
          noIntraClubMatches: true,
          distribution: 'random',
          seeding: 'random',
          constraints: { avoidSameClub: false, keepApart: [] },
          checkedInOnly: true,
          ...(o.leistungsgruppen ? { groupByTeamNumber: true } : {}),
        }),
      ];
    case 'ko':
      return [ko({ seedingSource: 'teamSeed' })];
    case 'partner_ko':
      // Gruppen zufällig (Harald: „wie jetzt zufällig mit der Möglichkeit zu verschieben“)
      return [groups({ layout: { groupSize: Math.max(4, o.groupSize) }, distribution: 'random', seeding: 'random', bestRemaining: Math.max(0, o.bestRemaining), partners: o.partners }), ko({})];
  }
}

export function modeOfPhases(phases: readonly PhaseConfig[]): ModeKey | 'custom' {
  const types = phases.map((p) => p.type).join('>');
  const first = phases[0];
  if (types === 'groups>single_elim' && first?.type === 'groups' && first.partners) return 'partner_ko';
  if (types === 'groups>single_elim' && first?.type === 'groups' && 'groupCount' in first.layout && first.layout.groupCount === 1) return 'rr_ko';
  if (types === 'groups>single_elim') return 'groups_ko';
  if (types === 'groups>ko_rounds') return 'groups_ko_rounds';
  if (types === 'groups' && first?.type === 'groups' && first.noIntraClubMatches) return 'club_rr';
  if (types === 'groups' && first?.type === 'groups' && 'groupCount' in first.layout) return 'roundrobin';
  if (types === 'single_elim') return 'ko';
  return 'custom';
}

export function isClubMode(phases: readonly PhaseConfig[]): boolean {
  return phases.some((p) => p.type === 'groups' && p.noIntraClubMatches);
}

export interface ModePreview {
  lines: string[];
  totalMatches: number;
  minutes: number;
}

/** Klartext-Vorschau wie in docs/06 §4: „6 Gruppen: 5×4, 1×3 · 33 Spiele · ca. 2 h 45 auf 4 Feldern" */
export function previewMode(
  mode: ModeKey,
  o: ModeOptions,
  teams: number,
  courts: number,
  matchMinutes: number,
  clubSizes?: number[],
  clubTeams?: readonly { name: string; club?: string }[],
): ModePreview {
  const lines: string[] = [];
  let total = 0;
  if (teams < 2) return { lines: [t('Mindestens 2 Teams')], totalMatches: 0, minutes: 0 };
  if (mode === 'club_rr' && o.leistungsgruppen) {
    // Leistungsgruppen (docs/13 §7): je Nummer eine Gruppe; ohne Liste die Annahme „drei gleich große Mannschaften, durchnummeriert“
    const stufen =
      clubTeams && clubTeams.length > 0
        ? tierMatchCount(clubTeams)
        : tierMatchCount(Array.from({ length: teams }, (_, i) => ({ name: `Mannschaft ${(i % 3) + 1} ${Math.floor(i / 3) + 1}`, club: `Mannschaft ${(i % 3) + 1}` })));
    total = stufen.matches;
    lines.push(
      t('{g} Leistungsgruppen nach Nummer im Teamnamen · {m} Spiele · {min}–{max} Spiele je Paar', {
        g: stufen.groups,
        m: total,
        min: Math.max(0, Math.min(...stufen.sizes) - 1),
        max: Math.max(0, Math.max(...stufen.sizes) - 1),
      }),
    );
    if (!clubTeams?.length) lines.push(t('(Annahme: gleich große Mannschaften — die Zahl wird nach dem Import genau)'));
  } else if (mode === 'club_rr') {
    const sizes = clubSizes && clubSizes.length > 0 ? clubSizes : [Math.ceil(teams / 3), Math.ceil(teams / 3), teams - 2 * Math.ceil(teams / 3)].filter((n) => n > 0);
    total = interClubMatchCount(sizes);
    const perTeam = sizes.map((n, i) => sizes.reduce((s, m, j) => (i === j ? s : s + m), 0));
    lines.push(
      t('{n} Mannschaften mit {sizes} Paaren · {m} Spiele · {min}–{max} Spiele je Paar', { n: sizes.length, sizes: sizes.join(' / '), m: total, min: Math.min(...perTeam), max: Math.max(...perTeam) }),
    );
    if (!clubSizes) lines.push(t('(Annahme: gleich große Mannschaften — die Zahl wird nach dem Import genau)'));
  } else if (mode === 'partner_ko') {
    const layout = groupLayout(teams, partnerLayoutSpec(teams, o.groupSize));
    const groupMatches = layout.sizes.reduce((s, n) => s + partnerMatchCount(n, o.partners), 0);
    total += groupMatches;
    const spiele = layout.sizes.map((n) => partnerGamesPerPlayer(n, o.partners));
    lines.push(
      t('{summary} · {m} Spiele · {min}–{max} Spiele je Spieler', {
        summary: tm(layout.summary),
        m: groupMatches,
        min: Math.min(...spiele),
        max: Math.max(...spiele),
      }),
    );
    if (layout.sizes.some((n) => n < 4)) lines.push(t('Achtung: Gruppen unter 4 Spielern haben keine Spiele — Gruppengröße ändern oder auffüllen'));
    if (o.partners === 'once' && layout.sizes.some((n) => n % 4 === 2 || n % 4 === 3))
      lines.push(t('„Jedes Paar einmal“ geht glatt nur mit 4, 5, 8 oder 9 Spielern je Gruppe — sonst spielt ein Paar nie zusammen'));
    const q = Math.min(teams, layout.count * o.qualifiersPerGroup + o.bestRemaining);
    const koMatches = Math.max(0, q - 1) + (o.thirdPlaceMatch && q >= 4 ? 1 : 0);
    total += koMatches;
    let size = 2;
    while (size < q) size *= 2;
    lines.push(t('KO mit {q} Spielern einzeln · {size}er-Bracket, {byes} Freilose · {m} Spiele', { q, size, byes: size - q, m: koMatches }));
  } else if (mode === 'groups_ko' || mode === 'roundrobin' || mode === 'rr_ko' || mode === 'groups_ko_rounds') {
    const layout = groupLayout(teams, mode === 'groups_ko' || mode === 'groups_ko_rounds' ? { groupSize: o.groupSize } : { groupCount: 1 });
    const groupMatches = layout.sizes.reduce((s, n) => s + roundRobinMatchCount(n), 0);
    total += groupMatches;
    lines.push(
      t('{summary} · {m} Spiele · {min}–{max} Spiele je Team', {
        summary: tm(layout.summary),
        m: groupMatches,
        min: (layout.sizes[layout.sizes.length - 1] ?? 1) - 1,
        max: (layout.sizes[0] ?? 1) - 1,
      }),
    );
    if (mode === 'groups_ko' || mode === 'rr_ko') {
      const q = mode === 'rr_ko' ? Math.min(teams, o.koTeams) : Math.min(teams, layout.count * o.qualifiersPerGroup + o.bestRemaining);
      const koMatches = Math.max(0, q - 1) + (o.thirdPlaceMatch && q >= 4 ? 1 : 0);
      total += koMatches;
      let size = 2;
      while (size < q) size *= 2;
      lines.push(
        mode === 'groups_ko' && o.bestRemaining > 0
          ? t('KO mit {q} Teams (Top {top} je Gruppe + {best} beste Nächstplatzierte) · {size}er-Bracket, {byes} Freilose · {m} Spiele', {
              q,
              top: o.qualifiersPerGroup,
              best: o.bestRemaining,
              size,
              byes: size - q,
              m: koMatches,
            })
          : t('KO mit {q} Teams · {size}er-Bracket, {byes} Freilose · {m} Spiele', { q, size, byes: size - q, m: koMatches }),
      );
    }
    if (mode === 'groups_ko_rounds') {
      // Keine erfundenen Spielzahlen: wie groß das KO wird, entscheidet die Leitung erst am Turniertag
      lines.push(
        t(
          'Danach KO Runde für Runde: vor jeder Runde legt die Leitung fest, wie viele Teams weiterspielen — Paarung cross, Runden wahlweise je Spielort. Die Zahl der KO-Spiele steht deshalb erst am Turniertag fest.',
        ),
      );
    }
  } else {
    const koMatches = teams - 1 + (o.thirdPlaceMatch && teams >= 4 ? 1 : 0);
    total += koMatches;
    let size = 2;
    while (size < teams) size *= 2;
    lines.push(t('{size}er-Bracket, {byes} Freilose · {m} Spiele', { size, byes: size - teams, m: koMatches }));
  }
  const perCourt = Math.max(1, courts);
  const minutes = (total * (matchMinutes + 2)) / perCourt;
  return { lines, totalMatches: total, minutes };
}
